import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import {
  doc,
  setDoc,
  getDoc,
  query,
  collection,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Timestamp } from "firebase/firestore";
import {
  Camera,
  Check,
  X,
  Loader2,
  User,
  Phone,
  AtSign,
  Mail,
  Shield,
  AlertCircle,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";
import { db, storage } from "../../firebase";

// Constants for styling
const COLORS = {
  primary: "from-[#14840c] to-green-600",
  error: "border-red-400 focus:border-red-500 focus:ring-red-100",
  success: "border-slate-200 focus:border-[#14840c] focus:ring-green-100",
};

const MiguelMXUserForm = ({ isEditing = false }) => {
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [existingUser, setExistingUser] = useState(null);

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    celular: "",
    fechaNacimiento: "",
    edad: "",
    sexo: "",
    nickname: "",
    profileImage: null,
    photoUrl: "",
    email: user?.email || "",
    uid: user?.uid || "",
  });

  const [errors, setErrors] = useState({
    nombre: "",
    apellido: "",
    celular: "",
    fechaNacimiento: "",
    sexo: "",
    nickname: "",
  });

  const [nicknameAvailable, setNicknameAvailable] = useState(null);

  // Load existing user data
  useEffect(() => {
    const loadUserData = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "Users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setExistingUser(userData);
            setFormData((prev) => ({
              ...prev,
              ...userData,
              email: user.email,
              uid: user.uid,
              fechaNacimiento: userData.fechaNacimiento
                ? userData.fechaNacimiento.toDate().toISOString().split("T")[0]
                : "",
              edad: userData.fechaNacimiento
                ? calculateAge(userData.fechaNacimiento.toDate())
                : "",
            }));
            if (userData.nickname) {
              setNicknameAvailable(true);
            }
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          setMessage({
            text: "Error al cargar datos del usuario",
            type: "error",
          });
        }
      }
    };
    loadUserData();
  }, [user]);

  // Calculate age from date
  const calculateAge = useCallback((birthDate) => {
    if (!birthDate) return "";
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age.toString();
  }, []);

  // Validate field
  const validateField = useCallback((name, value) => {
    switch (name) {
      case "nombre":
      case "apellido":
        if (!value) return "Este campo es obligatorio";
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s-]+$/.test(value))
          return "Solo letras, espacios y guiones son permitidos";
        return "";
      case "celular":
        if (!value) return "Este campo es obligatorio";
        if (!/^\+?[1-9]\d{1,14}$/.test(value))
          return "Ingresa un número de teléfono válido (ej. +521234567890)";
        return "";
      case "fechaNacimiento":
        if (!value) return "Este campo es obligatorio";
        const date = new Date(value);
        if (isNaN(date.getTime())) return "Fecha inválida";
        if (date > new Date()) return "La fecha no puede ser futura";
        return "";
      case "sexo":
        if (!value) return "Este campo es obligatorio";
        return "";
      case "nickname":
        if (!value) return "Este campo es obligatorio";
        if (!/^[a-z0-9_]{3,20}$/.test(value))
          return "Solo minúsculas, números y guiones bajos. Entre 3-20 caracteres.";
        return "";
      default:
        return "";
    }
  }, []);

  // Validate nickname with Firestore
  const validateNickname = useCallback(
    async (nickname) => {
      if (!nickname) {
        setNicknameAvailable(null);
        setErrors((prev) => ({
          ...prev,
          nickname: "Este campo es obligatorio",
        }));
        return false;
      }

      if (existingUser?.nickname === nickname) {
        setNicknameAvailable(true);
        setErrors((prev) => ({ ...prev, nickname: "" }));
        return true;
      }

      setCheckingNickname(true);
      const error = validateField("nickname", nickname);
      if (error) {
        setErrors((prev) => ({ ...prev, nickname: error }));
        setNicknameAvailable(false);
        setCheckingNickname(false);
        return false;
      }

      try {
        const q = query(
          collection(db, "Users"),
          where("nickname", "==", nickname)
        );
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          setErrors((prev) => ({
            ...prev,
            nickname: "Este nombre de usuario no está disponible",
          }));
          setNicknameAvailable(false);
          setCheckingNickname(false);
          return false;
        }

        setErrors((prev) => ({ ...prev, nickname: "" }));
        setNicknameAvailable(true);
        setCheckingNickname(false);
        return true;
      } catch (error) {
        console.error("Error checking nickname:", error);
        setErrors((prev) => ({
          ...prev,
          nickname: "Error al verificar el nombre de usuario",
        }));
        setNicknameAvailable(false);
        setCheckingNickname(false);
        return false;
      }
    },
    [existingUser, validateField]
  );

  // Handle input changes
  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "fechaNacimiento" ? { edad: calculateAge(value) } : {}),
      }));
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
      if (name === "nickname") {
        await validateNickname(value);
      }
    },
    [calculateAge, validateField, validateNickname]
  );

  // Handle image upload
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file && user?.uid) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: "La imagen debe ser menor a 5MB", type: "error" });
        return;
      }
      setUploading(true);
      try {
        const imageRef = ref(storage, `users/${user.uid}/profile.jpg`);
        await uploadBytes(imageRef, file);
        const downloadURL = await getDownloadURL(imageRef);
        setFormData((prev) => ({
          ...prev,
          profileImage: file,
          photoUrl: downloadURL,
        }));
        setMessage({ text: "Imagen subida correctamente", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      } catch (error) {
        console.error("Error uploading image:", error);
        setMessage({
          text: `Error al subir la imagen: ${error.message}`,
          type: "error",
        });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      } finally {
        setUploading(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!user?.uid) {
        throw new Error("Usuario no autenticado");
      }

      // Validate all fields
      const newErrors = {
        nombre: validateField("nombre", formData.nombre),
        apellido: validateField("apellido", formData.apellido),
        celular: validateField("celular", formData.celular),
        fechaNacimiento: validateField(
          "fechaNacimiento",
          formData.fechaNacimiento
        ),
        sexo: validateField("sexo", formData.sexo),
        nickname: validateField("nickname", formData.nickname),
      };

      if (
        Object.values(newErrors).some((error) => error) ||
        !nicknameAvailable
      ) {
        setErrors(newErrors);
        setMessage({
          text: "Por favor corrige los errores en el formulario",
          type: "error",
        });
        setLoading(false);
        return;
      }

      const userData = {
        nombre: formData.nombre,
        apellido: formData.apellido,
        celular: formData.celular,
        fechaNacimiento: formData.fechaNacimiento
          ? Timestamp.fromDate(new Date(formData.fechaNacimiento))
          : null,
        edad: formData.edad,
        sexo: formData.sexo,
        nickname: formData.nickname,
        photoUrl: formData.photoUrl,
        email: formData.email,
        uid: user.uid,
        updatedAt: Timestamp.now(),
      };

      if (!existingUser) {
        userData.createdAt = Timestamp.now();
      }

      await setDoc(doc(db, "Users", user.uid), userData, { merge: true });

      setMessage({
        text: isEditing
          ? "Perfil actualizado correctamente"
          : "¡Bienvenido a MiguelMX! Perfil configurado correctamente",
        type: "success",
      });
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      console.error("Error saving user data:", error);
      setMessage({
        text: `Error al guardar la información: ${error.message}`,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 py-2 z-10 shadow-sm">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex items-center text-slate-600 hover:text-[#14840c]"
            aria-label="Volver atrás"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <span className="text-lg font-bold text-[#14840c]">
            {isEditing ? "Editar Perfil" : "Configurar Perfil"}
          </span>
          <div className="w-10"></div> {/* Spacer for alignment */}
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 max-w-2xl mx-auto w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Photo */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Foto de Perfil
              </h3>
              <div className="text-center">
                <p className="text-slate-600 mb-4 text-base">
                  Una imagen profesional genera más confianza (opcional)
                </p>
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 border-4 border-white shadow-2xl">
                    {formData.photoUrl ? (
                      <img
                        src={formData.photoUrl}
                        alt="Foto de perfil"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="w-12 h-12 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <label
                    className="absolute bottom-0 right-0 bg-gradient-to-r from-[#14840c] to-green-600 rounded-full p-3 cursor-pointer hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:scale-110"
                    aria-label="Subir foto de perfil"
                  >
                    {uploading ? (
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5 text-white" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      disabled={uploading}
                      aria-label="Seleccionar imagen"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Información Personal
              </h3>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Nombre *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="nombre"
                    placeholder="Tu nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                      errors.nombre ? COLORS.error : COLORS.success
                    }`}
                    aria-label="Nombre"
                  />
                </div>
                {errors.nombre && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.nombre}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Apellido *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="apellido"
                    placeholder="Tu apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    required
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                      errors.apellido ? COLORS.error : COLORS.success
                    }`}
                    aria-label="Apellido"
                  />
                </div>
                {errors.apellido && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.apellido}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Fecha de Nacimiento *
                </label>
                <input
                  type="date"
                  name="fechaNacimiento"
                  value={formData.fechaNacimiento}
                  onChange={handleChange}
                  required
                  max={new Date().toISOString().split("T")[0]}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                    errors.fechaNacimiento ? COLORS.error : COLORS.success
                  }`}
                  aria-label="Fecha de nacimiento"
                />
                {formData.edad && (
                  <p className="text-sm text-[#14840c] mt-1 font-medium">
                    Edad: {formData.edad} años
                  </p>
                )}
                {errors.fechaNacimiento && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.fechaNacimiento}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Género *
                </label>
                <select
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  required
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                    errors.sexo ? COLORS.error : COLORS.success
                  }`}
                  aria-label="Género"
                >
                  <option value="">Seleccionar género</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                  <option value="otro">Otro</option>
                  <option value="prefiero_no_decir">Prefiero no decir</option>
                </select>
                {errors.sexo && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.sexo}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Información de Contacto
              </h3>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full pl-10 pr-12 py-3 bg-green-50 text-slate-600 border-2 border-green-200 rounded-xl font-medium text-base"
                    aria-label="Correo electrónico"
                  />
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
                </div>
                <p className="text-sm text-green-600 mt-1 flex items-center gap-1 font-medium">
                  <Check className="w-4 h-4" /> Email verificado y protegido
                </p>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Teléfono Móvil *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    name="celular"
                    placeholder="+52 123 456 7890"
                    value={formData.celular}
                    onChange={handleChange}
                    required
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                      errors.celular ? COLORS.error : COLORS.success
                    }`}
                    aria-label="Teléfono móvil"
                  />
                </div>
                {errors.celular && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.celular}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  📱 Incluye el código de país para mejor comunicación
                </p>
              </div>
            </div>

            {/* Username */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Nombre de Usuario
              </h3>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Usuario *
                </label>
                <div className="relative">
                  <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleChange}
                    required
                    placeholder="tu_usuario_123"
                    className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 transition-all bg-white shadow-sm text-base ${
                      errors.nickname ? COLORS.error : COLORS.success
                    }`}
                    aria-label="Nombre de usuario"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingNickname ? (
                      <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                    ) : nicknameAvailable ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : errors.nickname ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : null}
                  </div>
                </div>
                {errors.nickname && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.nickname}
                  </p>
                )}
                {nicknameAvailable && !errors.nickname && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" /> ¡Nombre de usuario
                    disponible!
                  </p>
                )}
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
                <h4 className="font-bold text-blue-800 mb-2">📋 Requisitos:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Solo minúsculas, números y guiones bajos (_)</li>
                  <li>• Entre 3 y 20 caracteres</li>
                  <li>• Sin espacios ni caracteres especiales</li>
                </ul>
              </div>
            </div>

            {/* Security Note */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 p-4 rounded-xl">
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-6 h-6 text-[#14840c]" />
                <p className="text-[#14840c] font-bold">Datos 100% Seguros</p>
              </div>
              <p className="text-green-700 text-sm text-center mt-2">
                Encriptación bancaria AES256 y certificación SSL
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={
                loading ||
                Object.values(errors).some((error) => error) ||
                !nicknameAvailable
              }
              className="w-full bg-gradient-to-r from-[#14840c] to-green-600 hover:from-green-600 hover:to-green-700 text-white p-4 rounded-xl font-bold text-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all duration-300"
              aria-label={isEditing ? "Actualizar perfil" : "Guardar perfil"}
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  {isEditing ? "Actualizando..." : "Guardando..."}
                </>
              ) : (
                <>
                  <Check className="w-6 h-6" />
                  {isEditing ? "Actualizar Perfil" : "Guardar Perfil"}
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Toast Message */}
      {message.text && (
        <div
          className={`fixed top-4 left-4 right-4 max-w-sm mx-auto p-4 rounded-xl shadow-2xl z-50 border-2 animate-in slide-in-from-top duration-300 ${
            message.type === "error"
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-green-50 border-green-200 text-green-800"
          }`}
          role="alert"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {message.type === "error" ? (
                <AlertCircle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              <span className="text-sm font-bold">{message.text}</span>
            </div>
            <button
              onClick={() => setMessage({ text: "", type: "" })}
              className="p-1 hover:bg-white/50 rounded-full transition-colors"
              aria-label="Cerrar mensaje"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiguelMXUserForm;
